import React, { useState, useEffect } from 'react';
import { API_URL } from '../../lib/api';

function AdminSettingsModal({ isOpen, onClose }) {
  const [adminData, setAdminData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    username: ''
  });
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('profile');
  const [emailLogs, setEmailLogs] = useState([]);
  const [emailLogsLoading, setEmailLogsLoading] = useState(false);
  const [activityLogs, setActivityLogs] = useState([]);
  const [activityLogsLoading, setActivityLogsLoading] = useState(false);
  const [activityCategory, setActivityCategory] = useState('');
  const [mfaStatus, setMfaStatus] = useState({ mfaEnabled: false, recoveryCodesRemaining: 0 });
  const [mfaSetupData, setMfaSetupData] = useState(null);
  const [mfaVerifyToken, setMfaVerifyToken] = useState('');
  const [mfaRecoveryCodes, setMfaRecoveryCodes] = useState(null);
  const [mfaDisablePassword, setMfaDisablePassword] = useState('');
  const [mfaMessage, setMfaMessage] = useState('');

  // Load admin data when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchAdminInfo();
      fetchMfaStatus();
    }
  }, [isOpen]);

  // Load email logs when emails tab is selected
  useEffect(() => {
    if (isOpen && activeTab === 'emails') {
      fetchEmailLogs();
    }
  }, [isOpen, activeTab]);

  useEffect(() => {
    if (isOpen && activeTab === 'activity') {
      fetchActivityLogs();
    }
  }, [isOpen, activeTab, activityCategory]);

  const fetchAdminInfo = async () => {
    try {
      const sessionToken = localStorage.getItem('sessionToken');
      const response = await fetch(`${API_URL}/api/admin/info`, {
        headers: sessionToken ? { 'X-Admin-Session': sessionToken } : {},
      });
      if (response.ok) {
        const data = await response.json();
        setAdminData({
          firstName: data.firstName || '',
          lastName: data.lastName || '',
          email: data.email || '',
          username: data.username || ''
        });
      }
    } catch (error) {
      console.error('Error fetching admin info:', error);
    }
  };

  const fetchEmailLogs = async () => {
    setEmailLogsLoading(true);
    try {
      const sessionToken = localStorage.getItem('sessionToken');
      const response = await fetch(`${API_URL}/api/admin/email-logs?limit=50`, {
        headers: {
          'X-Admin-Session': sessionToken
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setEmailLogs(data.logs || []);
      } else {
        console.error('Failed to fetch email logs');
        setEmailLogs([]);
      }
    } catch (error) {
      console.error('Error fetching email logs:', error);
      setEmailLogs([]);
    } finally {
      setEmailLogsLoading(false);
    }
  };

  const fetchActivityLogs = async () => {
    setActivityLogsLoading(true);
    try {
      const sessionToken = localStorage.getItem('sessionToken');
      const params = new URLSearchParams({ limit: '50' });
      if (activityCategory.trim()) params.set('category', activityCategory.trim());
      const response = await fetch(`${API_URL}/api/admin/activity-log?${params}`, {
        headers: { 'X-Admin-Session': sessionToken },
      });
      if (response.ok) {
        const data = await response.json();
        setActivityLogs(data.logs || []);
      } else {
        setActivityLogs([]);
      }
    } catch (error) {
      console.error('Error fetching activity logs:', error);
      setActivityLogs([]);
    } finally {
      setActivityLogsLoading(false);
    }
  };

  const fetchMfaStatus = async () => {
    try {
      const sessionToken = localStorage.getItem('sessionToken');
      const response = await fetch(`${API_URL}/api/admin/mfa/status`, {
        headers: { 'X-Admin-Session': sessionToken }
      });
      if (response.ok) {
        const data = await response.json();
        setMfaStatus(data);
      }
    } catch (error) {
      console.error('Error fetching MFA status:', error);
    }
  };

  const handleMfaSetup = async () => {
    setMfaMessage('');
    setMfaRecoveryCodes(null);
    try {
      const sessionToken = localStorage.getItem('sessionToken');
      const response = await fetch(`${API_URL}/api/admin/mfa/setup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Admin-Session': sessionToken }
      });
      const data = await response.json();
      if (response.ok) {
        setMfaSetupData(data);
      } else {
        setMfaMessage(data.error || 'MFA setup failed.');
      }
    } catch (error) {
      setMfaMessage('Network error during MFA setup.');
    }
  };

  const handleMfaVerifySetup = async () => {
    setMfaMessage('');
    try {
      const sessionToken = localStorage.getItem('sessionToken');
      const response = await fetch(`${API_URL}/api/admin/mfa/verify-setup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Admin-Session': sessionToken },
        body: JSON.stringify({ token: mfaVerifyToken })
      });
      const data = await response.json();
      if (response.ok && data.success) {
        setMfaRecoveryCodes(data.recoveryCodes);
        setMfaSetupData(null);
        setMfaVerifyToken('');
        fetchMfaStatus();
        setMfaMessage('MFA enabled successfully! Save your recovery codes.');
      } else {
        setMfaMessage(data.error || 'Verification failed.');
      }
    } catch (error) {
      setMfaMessage('Network error during MFA verification.');
    }
  };

  const handleMfaDisable = async () => {
    setMfaMessage('');
    if (!mfaDisablePassword) {
      setMfaMessage('Enter your password to disable MFA.');
      return;
    }
    if (!window.confirm('Are you sure you want to disable two-factor authentication?')) return;
    try {
      const sessionToken = localStorage.getItem('sessionToken');
      const response = await fetch(`${API_URL}/api/admin/mfa/disable`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Admin-Session': sessionToken },
        body: JSON.stringify({ password: mfaDisablePassword })
      });
      const data = await response.json();
      if (response.ok && data.success) {
        setMfaDisablePassword('');
        setMfaSetupData(null);
        setMfaRecoveryCodes(null);
        fetchMfaStatus();
        setMfaMessage('MFA has been disabled.');
      } else {
        setMfaMessage(data.error || 'Failed to disable MFA.');
      }
    } catch (error) {
      setMfaMessage('Network error.');
    }
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const sessionToken = localStorage.getItem('sessionToken');
      const response = await fetch(`${API_URL}/api/admin/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-Admin-Session': sessionToken
        },
        body: JSON.stringify(adminData)
      });

      const result = await response.json();
      if (result.success) {
        alert('Profile updated successfully!');
        fetchAdminInfo(); // Refresh data
      } else {
        alert('Error updating profile: ' + result.error);
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      alert('Error updating profile');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      alert('New passwords do not match');
      return;
    }
    
    setLoading(true);
    
    try {
      const sessionToken = localStorage.getItem('sessionToken');
      const response = await fetch(`${API_URL}/api/admin/password`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-Admin-Session': sessionToken
        },
        body: JSON.stringify({
          currentPassword: passwordData.currentPassword,
          newPassword: passwordData.newPassword
        })
      });

      const result = await response.json();
      if (result.success) {
        alert('Password changed successfully!');
        setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      } else {
        alert('Error changing password: ' + result.error);
      }
    } catch (error) {
      console.error('Error changing password:', error);
      alert('Error changing password');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content admin-settings-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>⚙️ Admin Settings</h2>
          <button className="close-button" onClick={onClose}>×</button>
        </div>
        
        <div className="modal-tabs">
          <button 
            className={`tab-button ${activeTab === 'profile' ? 'active' : ''}`}
            onClick={() => setActiveTab('profile')}
          >
            👤 Profile
          </button>
          <button 
            className={`tab-button ${activeTab === 'password' ? 'active' : ''}`}
            onClick={() => setActiveTab('password')}
          >
            🔒 Password
          </button>
          <button 
            className={`tab-button ${activeTab === 'security' ? 'active' : ''}`}
            onClick={() => setActiveTab('security')}
          >
            🔐 Security
          </button>
          <button 
            className={`tab-button ${activeTab === 'emails' ? 'active' : ''}`}
            onClick={() => setActiveTab('emails')}
          >
            📧 Email Logs
          </button>
          <button 
            className={`tab-button ${activeTab === 'activity' ? 'active' : ''}`}
            onClick={() => setActiveTab('activity')}
          >
            📋 Activity Log
          </button>
        </div>

        <div className="modal-body">
          {activeTab === 'profile' && (
            <form onSubmit={handleProfileSubmit} className="admin-form">
              <h3>Contact Information</h3>
              <div className="form-group">
                <label htmlFor="firstName">First Name</label>
                <input
                  type="text"
                  id="firstName"
                  value={adminData.firstName}
                  onChange={(e) => setAdminData({...adminData, firstName: e.target.value})}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="lastName">Last Name</label>
                <input
                  type="text"
                  id="lastName"
                  value={adminData.lastName}
                  onChange={(e) => setAdminData({...adminData, lastName: e.target.value})}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="email">Email</label>
                <input
                  type="email"
                  id="email"
                  value={adminData.email}
                  onChange={(e) => setAdminData({...adminData, email: e.target.value})}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="username">Username</label>
                <input
                  type="text"
                  id="username"
                  value={adminData.username}
                  onChange={(e) => setAdminData({...adminData, username: e.target.value})}
                  required
                />
              </div>
              <button type="submit" className="btn btn-primary" disabled={loading}>
                {loading ? 'Updating...' : 'Update Profile'}
              </button>
            </form>
          )}

          {activeTab === 'password' && (
            <form onSubmit={handlePasswordSubmit} className="admin-form">
              <h3>Change Password</h3>
              <div className="form-group">
                <label htmlFor="currentPassword">Current Password</label>
                <input
                  type="password"
                  id="currentPassword"
                  value={passwordData.currentPassword}
                  onChange={(e) => setPasswordData({...passwordData, currentPassword: e.target.value})}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="newPassword">New Password</label>
                <input
                  type="password"
                  id="newPassword"
                  value={passwordData.newPassword}
                  onChange={(e) => setPasswordData({...passwordData, newPassword: e.target.value})}
                  required
                  minLength="6"
                />
              </div>
              <div className="form-group">
                <label htmlFor="confirmPassword">Confirm New Password</label>
                <input
                  type="password"
                  id="confirmPassword"
                  value={passwordData.confirmPassword}
                  onChange={(e) => setPasswordData({...passwordData, confirmPassword: e.target.value})}
                  required
                  minLength="6"
                />
              </div>
              <button type="submit" className="btn btn-primary" disabled={loading}>
                {loading ? 'Changing...' : 'Change Password'}
              </button>
            </form>
          )}

          {activeTab === 'security' && (
            <div className="admin-form">
              <h3>Two-Factor Authentication (2FA)</h3>
              <p style={{ color: '#aaa', marginBottom: '16px' }}>
                Protect your admin account with an authenticator app (Google Authenticator, Authy, etc.)
              </p>
              
              {mfaMessage && (
                <div style={{ padding: '10px 14px', marginBottom: '14px', borderRadius: '6px',
                  background: mfaMessage.includes('success') || mfaMessage.includes('enabled') ? 'rgba(0,200,100,0.15)' : 
                              mfaMessage.includes('disabled') ? 'rgba(255,165,0,0.15)' : 'rgba(255,60,60,0.15)',
                  color: mfaMessage.includes('success') || mfaMessage.includes('enabled') ? '#4caf50' : 
                         mfaMessage.includes('disabled') ? '#ff9800' : '#f44336',
                  border: '1px solid currentColor', fontSize: '14px' }}>
                  {mfaMessage}
                </div>
              )}

              <div style={{ padding: '16px', borderRadius: '8px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', marginBottom: '16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div>
                    <strong>Status:</strong>{' '}
                    {mfaStatus.mfaEnabled ? (
                      <span style={{ color: '#4caf50' }}>Enabled</span>
                    ) : (
                      <span style={{ color: '#ff9800' }}>Not enabled</span>
                    )}
                  </div>
                  {mfaStatus.mfaEnabled && (
                    <div style={{ fontSize: '13px', color: '#aaa' }}>
                      Recovery codes remaining: <strong>{mfaStatus.recoveryCodesRemaining}</strong>
                    </div>
                  )}
                </div>
              </div>

              {!mfaStatus.mfaEnabled && !mfaSetupData && (
                <button onClick={handleMfaSetup} className="btn btn-primary" style={{ marginBottom: '16px' }}>
                  Enable Two-Factor Authentication
                </button>
              )}

              {mfaSetupData && (
                <div style={{ textAlign: 'center', padding: '16px', background: 'rgba(255,255,255,0.05)', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.1)' }}>
                  <p style={{ marginBottom: '12px', fontWeight: 'bold' }}>Scan this QR code with your authenticator app:</p>
                  <img src={mfaSetupData.qrCode} alt="MFA QR Code" style={{ maxWidth: '220px', borderRadius: '8px', background: '#fff', padding: '8px' }} />
                  <p style={{ margin: '12px 0 4px', fontSize: '13px', color: '#aaa' }}>
                    Or enter this key manually:
                  </p>
                  <code style={{ display: 'inline-block', padding: '6px 12px', background: 'rgba(0,0,0,0.3)', borderRadius: '4px', fontSize: '13px', letterSpacing: '2px', userSelect: 'all' }}>
                    {mfaSetupData.secret}
                  </code>
                  <div style={{ marginTop: '16px' }}>
                    <label htmlFor="mfaVerify" style={{ display: 'block', marginBottom: '6px', textAlign: 'left' }}>
                      Enter the 6-digit code from your app to verify:
                    </label>
                    <input
                      id="mfaVerify"
                      type="text"
                      inputMode="numeric"
                      maxLength={6}
                      value={mfaVerifyToken}
                      onChange={e => setMfaVerifyToken(e.target.value.replace(/\D/g, ''))}
                      placeholder="000000"
                      style={{ letterSpacing: '6px', textAlign: 'center', fontSize: '20px', maxWidth: '200px' }}
                    />
                    <div style={{ marginTop: '12px' }}>
                      <button onClick={handleMfaVerifySetup} className="btn btn-primary" disabled={mfaVerifyToken.length !== 6}>
                        Verify & Enable
                      </button>
                      <button onClick={() => { setMfaSetupData(null); setMfaVerifyToken(''); setMfaMessage(''); }} 
                        className="btn btn-secondary" style={{ marginLeft: '10px' }}>
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {mfaRecoveryCodes && (
                <div style={{ marginTop: '16px', padding: '16px', background: 'rgba(255,165,0,0.1)', border: '1px solid rgba(255,165,0,0.4)', borderRadius: '8px' }}>
                  <h4 style={{ color: '#ff9800', marginBottom: '8px' }}>Recovery Codes — Save These Now</h4>
                  <p style={{ fontSize: '13px', color: '#aaa', marginBottom: '12px' }}>
                    Each code can only be used once. Store them in a safe place.
                  </p>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px', fontFamily: 'monospace', fontSize: '15px' }}>
                    {mfaRecoveryCodes.map((code, i) => (
                      <div key={i} style={{ padding: '6px 10px', background: 'rgba(0,0,0,0.3)', borderRadius: '4px', textAlign: 'center' }}>
                        {code}
                      </div>
                    ))}
                  </div>
                  <button onClick={() => {
                    const text = mfaRecoveryCodes.join('\n');
                    navigator.clipboard.writeText(text).then(() => setMfaMessage('Recovery codes copied to clipboard.'));
                  }} className="btn btn-secondary" style={{ marginTop: '12px' }}>
                    Copy All Codes
                  </button>
                </div>
              )}

              {mfaStatus.mfaEnabled && !mfaSetupData && (
                <div style={{ marginTop: '20px', paddingTop: '16px', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                  <h4 style={{ color: '#f44336', marginBottom: '8px' }}>Disable Two-Factor Authentication</h4>
                  <div className="form-group">
                    <label htmlFor="mfaDisablePw">Enter your password to disable:</label>
                    <input
                      id="mfaDisablePw"
                      type="password"
                      value={mfaDisablePassword}
                      onChange={e => setMfaDisablePassword(e.target.value)}
                      placeholder="Current password"
                    />
                  </div>
                  <button onClick={handleMfaDisable} className="btn" disabled={!mfaDisablePassword}
                    style={{ background: '#c62828', color: '#fff', border: 'none', padding: '8px 20px', borderRadius: '6px', cursor: 'pointer' }}>
                    Disable MFA
                  </button>
                </div>
              )}
            </div>
          )}

          {activeTab === 'emails' && (
            <div className="admin-form">
              <h3>📧 Email Activity Logs</h3>
              <p>Track all welcome emails and password reset emails sent to users.</p>
              
              {emailLogsLoading ? (
                <div style={{textAlign: 'center', padding: '20px'}}>
                  <div>Loading email logs...</div>
                </div>
              ) : (
                <div className="email-logs-container">
                  {emailLogs.length === 0 ? (
                    <div style={{textAlign: 'center', padding: '20px', color: '#666'}}>
                      No email logs found.
                    </div>
                  ) : (
                    <div className="email-logs-table">
                      <div className="table-header">
                        <div className="col-date">Date/Time</div>
                        <div className="col-user">User</div>
                        <div className="col-type">Type</div>
                        <div className="col-email">Email</div>
                        <div className="col-status">Status</div>
                      </div>
                      
                      {emailLogs.map((log, index) => (
                        <div key={log.id || index} className={`table-row ${log.status}`}>
                          <div className="col-date">
                            {log.sentAt ? new Date(log.sentAt).toLocaleString() : '—'}
                          </div>
                          <div className="col-user">
                            {log.firstName || log.lastName ? (
                              <>
                                {log.firstName} {log.lastName}
                                <br />
                                <small>@{log.username || '—'}</small>
                              </>
                            ) : (
                              <small>—</small>
                            )}
                          </div>
                          <div className="col-type">
                            <span className={`email-type-badge ${log.emailType}`}>
                              {log.emailType === 'welcome' ? '👋 Welcome' : 
                               log.emailType === 'password_reset' ? '🔑 Password' : 
                               log.emailType || '—'}
                            </span>
                          </div>
                          <div className="col-email">
                            {log.recipientEmail || '—'}
                          </div>
                          <div className="col-status">
                            <span className={`status-badge ${log.status}`}>
                              {log.status === 'sent' ? '✅ Sent' : 
                               log.status === 'failed' ? '❌ Failed' : 
                               log.status || '—'}
                            </span>
                            {log.errorMessage && (
                              <div className="error-message" title={log.errorMessage}>
                                ⚠️ {log.errorMessage.substring(0, 50)}...
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  <div className="logs-footer">
                    <button 
                      onClick={fetchEmailLogs} 
                      className="btn btn-secondary"
                      disabled={emailLogsLoading}
                    >
                      🔄 Refresh Logs
                    </button>
                    <small style={{marginLeft: '10px', color: '#666'}}>
                      Showing last 50 email activities
                    </small>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'activity' && (
            <div className="admin-form">
              <h3>📋 Activity Log</h3>
              <p>System events: welcome emails, engagement sends, first logins, and alerts.</p>
              <div style={{ marginBottom: '12px', display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                <label htmlFor="activityCategoryFilter" style={{ fontSize: '13px' }}>Category:</label>
                <select
                  id="activityCategoryFilter"
                  value={activityCategory}
                  onChange={(e) => setActivityCategory(e.target.value)}
                  style={{ padding: '6px 10px', borderRadius: '6px' }}
                >
                  <option value="">All</option>
                  <option value="email">Email</option>
                  <option value="user">User</option>
                  <option value="alert">Alert</option>
                  <option value="system">System</option>
                </select>
                <button
                  type="button"
                  onClick={fetchActivityLogs}
                  className="btn btn-secondary"
                  disabled={activityLogsLoading}
                >
                  🔄 Refresh
                </button>
              </div>
              {activityLogsLoading ? (
                <div style={{ textAlign: 'center', padding: '20px' }}>Loading activity log…</div>
              ) : activityLogs.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>No activity recorded yet.</div>
              ) : (
                <div className="email-logs-container">
                  <div className="email-logs-table activity-logs-table">
                    <div className="table-header">
                      <div className="col-date">Date/Time</div>
                      <div className="col-type">Category</div>
                      <div className="col-user">Summary</div>
                      <div className="col-status">Actor</div>
                    </div>
                    {activityLogs.map((log) => (
                      <div key={log.id} className="table-row">
                        <div className="col-date">
                          {log.occurredAt ? new Date(log.occurredAt).toLocaleString() : '—'}
                        </div>
                        <div className="col-type">
                          <span className={`email-type-badge activity-cat-${log.category}`}>
                            {log.category || 'system'}
                          </span>
                        </div>
                        <div className="col-user" style={{ flex: 2 }}>
                          {log.summary}
                          {log.action && (
                            <div><small style={{ color: '#888' }}>{log.action}</small></div>
                          )}
                        </div>
                        <div className="col-status">{log.actor || 'system'}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default AdminSettingsModal;
